import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Crown, 
  Building2, 
  User, 
  Check, 
  Loader2, 
  Star,
  Zap,
  Shield,
  Gift,
  ArrowRight,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Subscription Plans Component
const SubscriptionPlans = () => {
  const { user, token, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [plans, setPlans] = useState({ business_plans: [], individual_plans: [] });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  
  // Check for success/cancel from Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId) {
      // Poll for checkout status
      pollCheckoutStatus(sessionId);
    }
    
    if (status === 'cancel') {
      toast.error("Your subscription checkout was canceled.");
    }
  }, [searchParams]);
  
  // Fetch plans and status on load
  useEffect(() => {
    fetchPlansAndStatus();
  }, [token]);
  
  const fetchPlansAndStatus = async () => {
    try {
      // Fetch plans (public)
      const plansRes = await fetch(`${API_URL}/api/subscriptions/plans`);
      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData);
      }
      
      // Fetch subscription status (if logged in)
      if (token) {
        const statusRes = await fetch(`${API_URL}/api/subscriptions/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setSubscriptionStatus(statusData);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const pollCheckoutStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 10;
    
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscriptions/checkout/status/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          
          if (data.payment_status === 'paid') {
            toast.success("Subscription Activated! Welcome, your subscription is now active.");
            refreshUser();
            fetchPlansAndStatus();
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking checkout status:', error);
      }
    };
    
    checkStatus();
  };
  
  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.error("Please sign in to subscribe.");
      navigate('/auth');
      return;
    }
    
    setCheckoutLoading(planId);
    
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }
      
      // Community Host (free) - no redirect needed
      if (!data.checkout_url) {
        toast.success(data.message || "Your plan is now active.");
        refreshUser();
        fetchPlansAndStatus();
        return;
      }
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCheckoutLoading(null);
    }
  };
  
  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to cancel subscription');
      }
      
      toast.success("Your subscription will end at the current billing period.");
      
      fetchPlansAndStatus();
      
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Determine which tab to show by default
  const defaultTab = user?.is_business ? 'business' : 'individual';
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Choose Your Plan</h1>
        <p className="text-muted-foreground">
          Support the community and add more to your presence
        </p>
      </div>
      
      {/* Current Subscription Status */}
      {subscriptionStatus?.has_subscription && (
        <Alert className="mb-6 border-primary/50 bg-primary/5">
          <Crown className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              <strong>Current Plan:</strong> {subscriptionStatus.subscription_tier} 
              {subscriptionStatus.cancel_at_period_end && ' (Canceling)'}
            </span>
            {!subscriptionStatus.cancel_at_period_end && (
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel Subscription
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
          <TabsTrigger value="individual" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Individual
          </TabsTrigger>
          <TabsTrigger value="business" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Business
          </TabsTrigger>
        </TabsList>
        
        {/* Individual Plans */}
        <TabsContent value="individual">
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {plans.individual_plans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative ${plan.id === 'plus_yearly' ? 'border-primary shadow-lg' : ''}`}
              >
                {plan.id === 'plus_yearly' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">
                      <Gift className="h-3 w-3 mr-1" />
                      Best Value
                    </Badge>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500" />
                    {plan.name}
                  </CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-foreground">${plan.price}</span>
                    <span className="text-muted-foreground">/{plan.interval}</span>
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter>
                  <Button 
                    className="w-full" 
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={checkoutLoading === plan.id || (subscriptionStatus?.subscription_tier === 'plus' && subscriptionStatus?.subscription_status === 'active')}
                  >
                    {checkoutLoading === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    {subscriptionStatus?.subscription_tier === 'plus' ? 'Current Plan' : 'Subscribe'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          {user?.is_business && (
            <Alert className="mt-6 max-w-3xl mx-auto">
              <Building2 className="h-4 w-4" />
              <AlertDescription>
                Individual Plus is for personal accounts. Switch to the Business tab for business plans.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
        
        {/* Business Plans */}
        <TabsContent value="business">
          <div className="grid md:grid-cols-3 gap-6">
            {plans.business_plans.map((plan, index) => (
              <Card 
                key={plan.id} 
                className={`relative ${plan.id === 'local_featured' ? 'border-primary shadow-lg' : ''}`}
              >
                {plan.id === 'local_featured' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">
                      <Zap className="h-3 w-3 mr-1" />
                      Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {plan.id === 'community_host' && <User className="h-5 w-5" />}
                    {plan.id === 'local_featured' && <Star className="h-5 w-5 text-yellow-500" />}
                    {plan.id === 'market_sponsor' && <Crown className="h-5 w-5 text-purple-500" />}
                    {plan.name}
                  </CardTitle>
                  <CardDescription>
                    {plan.price === 0 ? (
                      <span className="text-3xl font-bold text-foreground">Free</span>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-foreground">${plan.price}</span>
                        <span className="text-muted-foreground">/month</span>
                      </>
                    )}
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter>
                  <Button 
                    className="w-full" 
                    variant={plan.id === 'community_host' ? 'outline' : 'default'}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={
                      checkoutLoading === plan.id || 
                      (user?.business_visibility_tier === plan.id) ||
                      (!user?.is_business && plan.price > 0)
                    }
                  >
                    {checkoutLoading === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : user?.business_visibility_tier === plan.id ? (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    {user?.business_visibility_tier === plan.id ? 'Current Plan' : plan.price === 0 ? 'Get Started' : 'Subscribe'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          {!user?.is_business && (
            <Alert className="mt-6">
              <Building2 className="h-4 w-4" />
              <AlertDescription>
                Business plans require a business account. 
                <Button variant="link" className="p-0 h-auto ml-1" onClick={() => navigate('/settings/business')}>
                  Register as a business
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>
      
      {/* FAQ Section */}
      <div className="mt-16 max-w-2xl mx-auto">
        <h2 className="text-xl font-semibold mb-4 text-center">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-2">Can I cancel anytime?</h3>
            <p className="text-sm text-muted-foreground">
              Yes! You can cancel your subscription at any time. You&apos;ll retain access until the end of your current billing period.
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-2">What happens if my payment fails?</h3>
            <p className="text-sm text-muted-foreground">
              We&apos;ll notify you and attempt to process payment again. If unsuccessful, your account will be downgraded to the free tier while preserving your data.
            </p>
          </div>
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-2">How does Individual Plus differ from business tiers?</h3>
            <p className="text-sm text-muted-foreground">
              Individual Plus is for personal accounts and removes ads. Business tiers increase your visibility in the directory and enable event sponsorship features.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Success Page Component
export const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useContext(AuthContext);
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    if (sessionId) {
      // Refresh user data after successful payment
      refreshUser();
    }
  }, [sessionId, refreshUser]);
  
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <div className="mb-6">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
      </div>
      <h1 className="text-2xl font-bold mb-2">Subscription Activated!</h1>
      <p className="text-muted-foreground mb-6">
        Thank you for supporting LikeKind. Your subscription is now active.
      </p>
      <Button onClick={() => navigate('/')}>
        Return to Home
      </Button>
    </div>
  );
};

// Cancel Page Component  
export const SubscriptionCancel = () => {
  const navigate = useNavigate();
  
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <div className="mb-6">
        <XCircle className="h-16 w-16 text-muted-foreground mx-auto" />
      </div>
      <h1 className="text-2xl font-bold mb-2">Checkout Canceled</h1>
      <p className="text-muted-foreground mb-6">
        Your subscription checkout was canceled. No charges were made.
      </p>
      <div className="space-x-4">
        <Button variant="outline" onClick={() => navigate('/')}>
          Return to Home
        </Button>
        <Button onClick={() => navigate('/subscription')}>
          View Plans
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
